<template>
  <img
    v-if="showCustomLogo"
    :alt="brandName"
    class="gl-brand-logo"
    :class="`gl-brand-logo--${variant}`"
    :src="customUrl"
    v-on="$listeners"
    @error="onCustomLogoError"
  >
  <component
    :is="fallbackComponent"
    v-else
    v-on="$listeners"
  />
</template>

<script>
// utils
import config from '@/utils/appConfig'
import { BRAND_NAME, BRAND_LOGO_URL, BRAND_LOGO_SHORT_URL } from '@/utils/brand'

const LogoFull = () => {
  const prefix = config.VUE_APP_THEME || 'default'
  return import(`@/assets/svg/header/${prefix}-logo.svg?vue-component`)
}

const LogoFullNY = () => {
  const prefix = config.VUE_APP_THEME || 'default'
  return import(`@/assets/svg/header/${prefix}-logo-ny.svg?vue-component`)
}

const LogoShort = () => {
  return import(`@/assets/svg/header/GLNewLogoShort.svg?vue-component`)
}

const LogoShort24 = () => {
  return import(`@/assets/svg/header/GLNewLogoShort24.svg?vue-component`)
}

export default {
  name: 'GlBrandLogo',
  components: {
    LogoFull,
    LogoFullNY,
    LogoShort,
    LogoShort24,
  },
  props: {
    variant: {
      type: String,
      default: 'full',
      validator: value => ['full', 'short', 'short-24'].includes(value),
    },
    // New Year easter egg, only honoured by the bundled full logo.
    ny: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      customLogoFailed: false,
    }
  },
  computed: {
    brandName() {
      return BRAND_NAME
    },
    // A configured URL wins over the NY variant — a white-labelled deployment
    // should never fall back to our seasonal logo. For the same reason the short
    // mark falls back to the full logo when only one URL is configured, rather
    // than to our own bundled mark.
    customUrl() {
      if (this.variant === 'full') return BRAND_LOGO_URL

      return BRAND_LOGO_SHORT_URL || BRAND_LOGO_URL
    },
    fallbackComponent() {
      if (this.variant === 'short') return 'LogoShort'
      if (this.variant === 'short-24') return 'LogoShort24'
      return this.ny ? 'LogoFullNY' : 'LogoFull'
    },
    showCustomLogo() {
      return Boolean(this.customUrl) && !this.customLogoFailed
    },
  },
  watch: {
    customUrl() {
      this.customLogoFailed = false
    },
  },
  methods: {
    // An unreachable or misconfigured URL must not leave a broken image in the
    // sidebar — drop back to the bundled SVG instead.
    onCustomLogoError() {
      this.customLogoFailed = true
    },
  },
}
</script>

<style>
.gl-brand-logo {
  display: block;
  object-fit: contain;
}
.gl-brand-logo--full {
  width: auto;
  height: 36px;
  max-width: 100%;
}
.gl-brand-logo--short {
  width: 36px;
  height: 36px;
}
.gl-brand-logo--short-24 {
  width: 24px;
  height: 24px;
}
</style>
